import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ShoppingCart, Lock, Truck, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { countries } from '../data/countries';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, getCartTotal, clearCart } = useCart();
  
  // Default windmill image for fallback
  const defaultWindmillImage = "https://customer-assets-39nsmqrw.emergentagent.net/job_visual-product-hub/artifacts/beot2jig_IMG-20260812-WA0049.jpg";

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    gstNumber: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [shippingCost, setShippingCost] = useState(0);

  // Redirect if cart is empty
  useEffect(() => {
    if (cart.length === 0) {
      navigate('/');
    }
  }, [cart, navigate]);

  // Calculate shipping cost based on country and item count
  useEffect(() => {
    const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
    const country = formData.country;
    
    if (country.toLowerCase() === 'india') {
      setShippingCost(0);
    } else {
      // International: ₹1,000 per 5 items
      const shippingBatches = Math.ceil(totalItems / 5);
      setShippingCost(shippingBatches * 1000);
    }
  }, [formData.country, cart]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error for this field
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.street.trim()) newErrors.street = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State/Province is required';
    if (!formData.pincode.trim()) newErrors.pincode = 'Postal/Zip code is required';
    if (!formData.country) newErrors.country = 'Country is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        alert('Failed to load payment gateway. Please try again.');
        setLoading(false);
        return;
      }

      // Create order on backend with cart items
      const orderData = {
        customer: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: {
            street: formData.street,
            city: formData.city,
            state: formData.state,
            pincode: formData.pincode,
            country: formData.country
          },
          gst_number: formData.gstNumber || null
        },
        items: cart.map(item => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          price: item.price,
          image_url: item.image_url
        })),
        subtotal: getCartTotal(),
        shipping_cost: shippingCost,
        total_amount: getCartTotal() + shippingCost,
        currency: "INR"
      };

      const { data } = await axios.post(`${API_URL}/api/orders`, orderData);

      // Razorpay options
      const options = {
        key: data.razorpay_key_id,
        amount: data.amount,
        currency: data.currency,
        name: "MADTOYS",
        description: cart.length === 1 ? cart[0].name : `MADTOYS Order (${cart.length} items)`,
        image: "https://customer-assets.emergentagent.com/job_visual-product-hub/artifacts/pa9qq6l3_61my-6dSnvL.jpg",
        order_id: data.razorpay_order_id,
        handler: async function (response) {
          try {
            // Verify payment on backend
            const verifyResponse = await axios.post(`${API_URL}/api/orders/verify-payment`, {
              order_id: data.order.id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            if (verifyResponse.data.success) {
              // Clear cart after successful payment
              clearCart();
              navigate('/order-success', {
                state: {
                  order: verifyResponse.data.order
                }
              });
            }
          } catch (error) {
            console.error('Payment verification failed:', error);
            alert('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone
        },
        notes: {
          address: `${formData.street}, ${formData.city}, ${formData.state} - ${formData.pincode}`
        },
        theme: {
          color: "#D4AF37"
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to initialize payment. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-primary">
      {/* Header */}
      <motion.nav 
        className="fixed top-0 left-0 right-0 z-50 bg-dark-primary/80 backdrop-blur-lg border-b border-gold/10"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
      >
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-300 hover:text-gold transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-xl font-bold">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-gold" />
            <span className="text-sm text-gray-400">Secure Checkout</span>
          </div>
        </div>
      </motion.nav>

      {/* Main Content */}
      <div className="container mx-auto px-6 pt-32 pb-20">
        <div className="max-w-6xl mx-auto">
          <motion.h1 
            className="text-4xl font-bold text-white mb-8 flex items-center gap-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ShoppingCart className="w-10 h-10 text-gold" />
            Checkout
          </motion.h1>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Checkout Form */}
            <motion.div 
              className="lg:col-span-2"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="bg-dark-secondary border-gold/10">
                <CardContent className="p-8">
                  <form onSubmit={handlePayment} className="space-y-6">
                    {/* Contact Information */}
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-4">Contact Information</h2>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="name" className="text-gray-300">Full Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className="bg-dark-primary border-gold/20 text-white mt-2"
                            placeholder="John Doe"
                          />
                          {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-gray-300">Email *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="bg-dark-primary border-gold/20 text-white mt-2"
                            placeholder="john@example.com"
                          />
                          {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
                        </div>
                        <div>
                          <Label htmlFor="phone" className="text-gray-300">Phone Number *</Label>
                          <Input
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="bg-dark-primary border-gold/20 text-white mt-2"
                            placeholder="9876543210"
                            maxLength="10"
                          />
                          {errors.phone && <p className="text-red-400 text-sm mt-1">{errors.phone}</p>}
                        </div>
                      </div>
                    </div>

                    {/* Shipping Address */}
                    <div className="pt-6 border-t border-gold/10">
                      <h2 className="text-2xl font-bold text-white mb-4">Shipping Address</h2>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="country" className="text-gray-300">Country *</Label>
                          <select
                            id="country"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            className="w-full bg-dark-primary border border-gold/20 text-white rounded-lg p-3 mt-2 focus:outline-none focus:border-gold"
                          >
                            {countries.map(country => (
                              <option key={country} value={country}>{country}</option>
                            ))}
                          </select>
                          {errors.country && <p className="text-red-400 text-sm mt-1">{errors.country}</p>}
                        </div>
                        <div>
                          <Label htmlFor="street" className="text-gray-300">Street Address *</Label>
                          <Input
                            id="street"
                            name="street"
                            value={formData.street}
                            onChange={handleChange}
                            className="bg-dark-primary border-gold/20 text-white mt-2"
                            placeholder="House No., Street, Area"
                          />
                          {errors.street && <p className="text-red-400 text-sm mt-1">{errors.street}</p>}
                        </div>
                        <div className="grid md:grid-cols-3 gap-4">
                          <div>
                            <Label htmlFor="city" className="text-gray-300">City *</Label>
                            <Input
                              id="city"
                              name="city"
                              value={formData.city}
                              onChange={handleChange}
                              className="bg-dark-primary border-gold/20 text-white mt-2"
                              placeholder="City"
                            />
                            {errors.city && <p className="text-red-400 text-sm mt-1">{errors.city}</p>}
                          </div>
                          <div>
                            <Label htmlFor="state" className="text-gray-300">State/Province *</Label>
                            <Input
                              id="state"
                              name="state"
                              value={formData.state}
                              onChange={handleChange}
                              className="bg-dark-primary border-gold/20 text-white mt-2"
                              placeholder="State/Province"
                            />
                            {errors.state && <p className="text-red-400 text-sm mt-1">{errors.state}</p>}
                          </div>
                          <div>
                            <Label htmlFor="pincode" className="text-gray-300">Postal/Zip Code *</Label>
                            <Input
                              id="pincode"
                              name="pincode"
                              value={formData.pincode}
                              onChange={handleChange}
                              className="bg-dark-primary border-gold/20 text-white mt-2"
                              placeholder="Postal Code"
                            />
                            {errors.pincode && <p className="text-red-400 text-sm mt-1">{errors.pincode}</p>}
                          </div>
                        </div>
                        {formData.country === 'India' && (
                          <div>
                            <Label htmlFor="gstNumber" className="text-gray-300">GST Number (Optional)</Label>
                            <Input
                              id="gstNumber"
                              name="gstNumber"
                              value={formData.gstNumber}
                              onChange={handleChange}
                              className="bg-dark-primary border-gold/20 text-white mt-2"
                              placeholder="22AAAAA0000A1Z5"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gold hover:bg-gold-dark text-dark-primary font-bold text-lg py-8 mt-8"
                    >
                      {loading ? 'Processing...' : `Pay ₹${(getCartTotal() + shippingCost).toLocaleString('en-IN')}`}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            {/* Order Summary */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="bg-dark-secondary border-gold/10 sticky top-24">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-white mb-6">Order Summary</h2>
                  
                  <div className="space-y-4">
                    {/* Cart Items */}
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {cart.map((item) => (
                        <div key={item.id} className="flex gap-4 pb-3 border-b border-gold/10">
                          <img 
                            src={item.image_url} 
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded-lg border-2 border-gold/20 flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-semibold text-sm line-clamp-2">{item.name}</h3>
                            <p className="text-gray-400 text-sm">Qty: {item.quantity}</p>
                            <p className="text-gold font-bold text-sm">₹{(item.price * item.quantity).toLocaleString('en-IN')}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="border-t border-gold/10 pt-4 space-y-2">
                      <div className="flex justify-between text-gray-300">
                        <span>Subtotal ({cart.reduce((sum, item) => sum + item.quantity, 0)} items)</span>
                        <span>₹{getCartTotal().toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between text-gray-300">
                        <span>Shipping ({formData.country})</span>
                        {shippingCost === 0 ? (
                          <span className="text-green-400">FREE</span>
                        ) : (
                          <span>₹{shippingCost.toLocaleString('en-IN')}</span>
                        )}
                      </div>
                      <div className="border-t border-gold/10 pt-2 mt-2">
                        <div className="flex justify-between text-white text-xl font-bold">
                          <span>Total</span>
                          <span className="text-gold">₹{(getCartTotal() + shippingCost).toLocaleString('en-IN')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-dark-primary rounded-lg p-4 mt-6 space-y-3">
                      <div className="flex items-center gap-3 text-gray-300">
                        <Truck className="w-5 h-5 text-gold" />
                        <span className="text-sm">
                          {formData.country.toLowerCase() === 'india' 
                            ? 'Free Shipping in India' 
                            : `₹${shippingCost.toLocaleString('en-IN')} International Shipping`}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-300">
                        <Shield className="w-5 h-5 text-gold" />
                        <span className="text-sm">Secure Payment</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-300">
                        <Lock className="w-5 h-5 text-gold" />
                        <span className="text-sm">7-Day Returns</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
